import { useFormik } from 'formik';
import { equals } from 'ramda';
import { Stack, TextField, Typography, Button, CircularProgress } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { ConfirmationDialog, CourseFormValues, DATE_FORMAT, TIMEZONE_MESSAGE } from '@shared';
import { courseValidationSchema } from './schema/schema';
import { useState } from 'react';

type PropTypes = {
  initialValues: CourseFormValues;
  isLoading: boolean;
  onSubmit: (values: CourseFormValues) => void;
  onExit: () => void;
  title: string;
  submitButtonLabel: string;
};

export const Form = ({
  initialValues,
  isLoading,
  onSubmit,
  onExit,
  title,
  submitButtonLabel,
}: PropTypes) => {
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);

  const toggleConfirmationOpen = () => setConfirmationOpen((prev) => !prev);

  const handleApproveConfirm = () => {
    toggleConfirmationOpen();
    onExit();
  };

  const {
    values,
    handleChange,
    setFieldValue,
    handleBlur,
    touched,
    errors,
    handleSubmit,
    isValid,
  } = useFormik({
    initialValues,
    validationSchema: courseValidationSchema,
    validateOnBlur: true,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  const isUnsavedChanges = equals(values, initialValues);

  return (
    <form onSubmit={handleSubmit}>
      <Typography variant='h6' mb={5}>
        {title}
      </Typography>
      <Stack spacing={2} alignItems='stretch' mb={2}>
        <TextField
          fullWidth
          required
          autoFocus
          name='name'
          label='Course Name'
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.name && Boolean(errors.name)}
          helperText={touched.name && errors.name}
          value={values.name}
        />
        <TextField
          multiline
          fullWidth
          name='description'
          label='Course Description (optional)'
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.description}
        />
        <DateTimePicker
          name='start'
          label='Start Date (optional)'
          onChange={(value) => setFieldValue('start', value)}
          value={values.start}
          format={DATE_FORMAT}
          ampm={false}
          slotProps={{
            textField: {
              onBlur: handleBlur,
              fullWidth: true,
              variant: 'outlined',
              helperText: (touched.start && errors.start) || (values.start && TIMEZONE_MESSAGE),
              error: touched.start && Boolean(errors.start),
            },
          }}
        />
        <DateTimePicker
          name='end'
          label='End Date (optional)'
          onChange={(value) => setFieldValue('end', value)}
          value={values.end}
          format={DATE_FORMAT}
          ampm={false}
          slotProps={{
            textField: {
              onBlur: handleBlur,
              fullWidth: true,
              variant: 'outlined',
              helperText: (touched.end && errors.end) || (values.end && TIMEZONE_MESSAGE),
              error: touched.end && Boolean(errors.end),
            },
          }}
        />
      </Stack>
      <Stack direction='row' spacing={1}>
        <Button onClick={!isUnsavedChanges ? toggleConfirmationOpen : onExit} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant='contained' disabled={!isValid} type='submit'>
          {isLoading ? <CircularProgress size={18} color='inherit' /> : submitButtonLabel}
        </Button>
      </Stack>
      <ConfirmationDialog
        isConfirmationOpen={isConfirmationOpen}
        title='Unsaved changes'
        text='This page contains unsaved changes. Do you still wish to leave the page?'
        labelButton='Leave'
        toggleConfirmationOpen={toggleConfirmationOpen}
        handleApproveConfirm={handleApproveConfirm}
      />
    </form>
  );
};
